import { combineReducers, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { ResourceCategoryResult } from '@mews-distributor/data-access';
import { keyById } from '@mews/fp';
import { CategoriesSlice } from './types';

const dataInitialState: CategoriesSlice['data'] = {};
const metaInitialState: CategoriesSlice['meta'] = {};

interface FetchResourceCategoriesSucceededPayload extends ResourceCategoryResult {
    enterpriseId: string;
}

const resourceCategoriesDataSlice = createSlice({
    name: 'resourceCategoriesData',
    initialState: dataInitialState,
    reducers: {
        fetchResourceCategoriesSucceeded: (
            state,
            { payload: { resourceCategories, enterpriseId } }: PayloadAction<FetchResourceCategoriesSucceededPayload>
        ) =>
            pipe(
                resourceCategories,
                array.map((category) => ({ ...category, enterpriseId })),
                keyById,
                (extendedCategories) => ({ ...state, ...extendedCategories })
            ),
    },
});

export const { fetchResourceCategoriesSucceeded } = resourceCategoriesDataSlice.actions;

interface MetaPayload {
    serviceId: string;
}

const resourceCategoriesMetaSlice = createSlice({
    name: 'resourceCategoriesMeta',
    initialState: metaInitialState,
    reducers: {
        fetchResourceCategoriesStarted: (state, { payload: { serviceId } }: PayloadAction<MetaPayload>) => {
            state[serviceId] = { hasFailed: false, isFetching: true };
        },
        fetchResourceCategoriesFailed: (state, { payload: { serviceId } }: PayloadAction<MetaPayload>) => {
            state[serviceId] = { hasFailed: true, isFetching: false };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchResourceCategoriesSucceeded, (state, { payload: { resourceCategories } }) => {
            const serviceId = resourceCategories[0]?.serviceId;
            if (serviceId != null) {
                state[serviceId] = { hasFailed: false, isFetching: false };
            }
        });
    },
});

export const resourceCategoriesReducer = combineReducers({
    data: resourceCategoriesDataSlice.reducer,
    meta: resourceCategoriesMetaSlice.reducer,
});

export const { fetchResourceCategoriesStarted, fetchResourceCategoriesFailed } = resourceCategoriesMetaSlice.actions;
